import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import type { HerkomstPlek } from "../api/insights";
import { WarmteLaag } from "./warmteLaag";

export type Weergave = "WARMTE" | "BOLLEN";

const NL_MIDDEN: [number, number] = [52.15, 5.4];
const MERKKLEUR = "#2f6d5a";

/**
 * De kaart onder de herkomstkaart: OpenStreetMap met een warmtelaag of met bollen per plek.
 *
 * <p>In beide weergaven ligt er per plek een cirkel met een tooltip ("Enschede: 336 opnames"). Bij de
 * warmte is die onzichtbaar: een warmtevlek zegt waar het druk is, maar niet hoeveel, en dat wil je
 * weten zodra je met de muis op de rode vlek staat.
 */
export function HerkomstKaartMap({
  plekken,
  weergave,
  labelVan,
  eenheid,
  pasSleutel,
  volledig,
}: {
  plekken: HerkomstPlek[];
  weergave: Weergave;
  labelVan: (p: HerkomstPlek) => string;
  eenheid: string;
  /** Verandert als de kaart opnieuw om de plekken moet passen, zoals bij een ander detailniveau. */
  pasSleutel: string;
  /** Of alle plekken bekend zijn. Zolang er nog worden opgezocht, blijft de kaart meeschuiven. */
  volledig: boolean;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const kaartRef = useRef<L.Map | null>(null);
  const groepRef = useRef<L.LayerGroup | null>(null);
  const warmteRef = useRef<WarmteLaag | null>(null);
  const gepast = useRef(false);

  useEffect(() => {
    if (!hostRef.current || kaartRef.current) return;
    const kaart = L.map(hostRef.current, { scrollWheelZoom: true }).setView(NL_MIDDEN, 7);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap contributors",
    }).addTo(kaart);
    groepRef.current = L.layerGroup().addTo(kaart);
    kaartRef.current = kaart;
    // Het tabblad is net zichtbaar geworden; pas daarna klopt de maat.
    setTimeout(() => kaart.invalidateSize(), 0);
    return () => {
      kaart.remove();
      kaartRef.current = null;
      groepRef.current = null;
      warmteRef.current = null;
    };
  }, []);

  useEffect(() => {
    gepast.current = false;
  }, [pasSleutel]);

  useEffect(() => {
    const kaart = kaartRef.current;
    const groep = groepRef.current;
    if (!kaart || !groep) return;
    groep.clearLayers();
    warmteRef.current?.remove();
    warmteRef.current = null;

    const max = plekken.reduce((m, p) => Math.max(m, p.aantal), 0);
    if (weergave === "WARMTE" && plekken.length > 0) {
      warmteRef.current = new WarmteLaag(plekken).addTo(kaart);
    }
    for (const p of plekken) {
      const bol = weergave === "BOLLEN";
      const cirkel = L.circleMarker([p.lat, p.lon], {
        // Oppervlak en niet straal evenredig met het aantal: een bol met twee keer zoveel dieren oogt
        // anders vier keer zo groot.
        radius: bol ? 4 + 22 * Math.sqrt(max > 0 ? p.aantal / max : 0) : 10,
        color: MERKKLEUR,
        weight: 1,
        opacity: bol ? 0.9 : 0,
        fillColor: MERKKLEUR,
        fillOpacity: bol ? 0.4 : 0,
      });
      // Als tekstknoop en niet als HTML: de naam komt uit de database.
      const tekst = document.createElement("span");
      tekst.textContent = `${labelVan(p)}: ${p.aantal.toLocaleString("nl-NL")} ${eenheid}`;
      cirkel.bindTooltip(tekst);
      groep.addLayer(cirkel);
    }
    if (plekken.length > 0 && !gepast.current) {
      kaart.fitBounds(L.latLngBounds(plekken.map((p) => [p.lat, p.lon] as [number, number])), {
        padding: [24, 24],
        maxZoom: 11,
      });
      gepast.current = volledig;
    }
    // labelVan en eenheid veranderen niet los van de plekken.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plekken, weergave, volledig]);

  return <div ref={hostRef} className="fawkes-herkomst__map" />;
}
